import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import DynamicForm from "../common/DynamicForm";
import { AlertDialog } from "../common/AlertDailog";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useParams } from "react-router-dom";
import FormFields from "./fields.json";
import { codelistValueSchema } from "../../config/schemas/codelistValueSchema";
import { useCodelist } from "../../hooks/useCodelist";
import { Drawer } from "@mui/material";
import AddReasonForm from "../common/AddReasonForm";
import { useGetChangedFields } from "../../hooks/useGetChangedFields";
import { usePermissionWrapper } from "../../hooks/usePermissionWrapper";

const CodelistValuesDialog: React.FunctionComponent<any> = (props) => {
  const {
    openCodelistDialog,
    setOpenCodelistDialog,
    isEdit,
    setIsEdit,
    codeDetails,
    setCodeDetails,
    onSuccess,
  } = props;
  const params = useParams();
  const { checkPermission } = usePermissionWrapper();
  const values = isEdit ? codeDetails : {};
  const {
    createCodelistValues,
    createCodelistValuesData,
    createCodelistValuesIsError,
    createCodelistValuesError,
    createCodelistValuesIsSuccess,
    updateCodelistValues,
    updateCodelistValuesData,
    updateCodelistValuesIsError,
    updateCodelistValuesError,
    updateCodelistValuesIsSuccess,
  } = useCodelist();
  const { showSuccess, handleError } = useSnackbar();
  type FormValues = z.infer<typeof codelistValueSchema>;
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    setFocus,
    reset,
    watch,
    getValues,
  } = useForm<FormValues>({
    values,
    resolver: zodResolver(codelistValueSchema),
    defaultValues: {},
  });

  const onInvalid = (errors: any) => console.debug(errors);
  const submit = (data: any, updatedDataObj?: any) => {
    const dataObj = checkPermission("OVERRIDE_AUDIT_REASON")
      ? data
      : { ...data, updated_fields: updatedDataObj };
    isEdit ? handleUpdateCode(dataObj) : handleCreateCode(dataObj);
  };
  const handleCreateCode = (payload: any) => {
    createCodelistValues({
      code_list_code: params?.code_list_code,
      data: {
        ...payload,
      },
    });
  };
  const handleUpdateCode = (payload: any) => {
    updateCodelistValues({
      code_list_code: params?.code_list_code,
      data: {
        ...payload,
      },
    });
  };
  const onCloseDialog = () => {
    setIsEdit(false);
    setCodeDetails({});
    reset();
    setOpenCodelistDialog(false);
  };
  useEffect(() => {
    const firstError = Object.keys(errors)?.reduce((field: any, a) => {
      return (errors as any)[field] ? field : a;
    }, null);
    if (firstError) {
      setFocus(firstError);
    }
  }, [errors, setFocus]);
  useEffect(() => {
    if (createCodelistValuesIsSuccess) {
      const successMsg = createCodelistValuesData;
      showSuccess(successMsg?.details, "create-codelist-code-success");
      onSuccess();
      onCloseDialog();
    }
  }, [createCodelistValuesIsSuccess]);
  useEffect(() => {
    if (updateCodelistValuesIsSuccess) {
      const successMsg = updateCodelistValuesData;
      showSuccess(successMsg?.details, "update-codelist-code-success");
      onSuccess();
      onCloseDialog();
    }
  }, [updateCodelistValuesIsSuccess]);
  useEffect(() => {
    if (createCodelistValuesIsError || updateCodelistValuesIsError) {
      const errorMsg: any =
        createCodelistValuesError ?? updateCodelistValuesError;
      handleError(errorMsg);
    }
  }, [createCodelistValuesIsError, updateCodelistValuesIsError]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }
      setDrawerOpen(open);
    };

  const editedData: any = useGetChangedFields(
    control,
    watch,
    FormFields?.updateFields,
    getValues,
    values
  );

  const handleAddReason = (updatedDataObj: any) => {
    submit(getValues(), updatedDataObj);
  };

  return (
    <AlertDialog
      open={openCodelistDialog}
      title={`${isEdit ? "Edit" : "Add"} Value Code`}
      handleClose={onCloseDialog}
      handleConfirm={
        checkPermission("OVERRIDE_AUDIT_REASON")
          ? handleSubmit(submit, onInvalid)
          : toggleDrawer(true)
      }
      maxWidth="md"
      saveDisable={editedData?.length == 0}
    >
      <form onSubmit={handleSubmit(submit, onInvalid)}>
        <DynamicForm
          fieldsJson={
            isEdit ? FormFields?.updateFields : FormFields?.createFields
          }
          errors={errors}
          register={register}
          control={control}
        />
      </form>
      <div
        id="drawer-container"
        style={{ display: drawerOpen ? "block" : "none" }}
      />
      {drawerOpen && (
        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={toggleDrawer(false)}
          style={{ zIndex: "1300" }}
          PaperProps={{ style: { position: "absolute" } }}
          ModalProps={{
            container: document.getElementById("drawer-container"),
            style: { position: "absolute", background: "rgba(0,0,0,0.3)" },
          }}
        >
          <AddReasonForm
            toggleDrawer={toggleDrawer}
            editedData={editedData}
            handleAddReason={handleAddReason}
          />
        </Drawer>
      )}
    </AlertDialog>
  );
};

export default CodelistValuesDialog;
